// theme.ts
import {
    defineConfig,
    defineTokens,
    defaultConfig,
    defineSlotRecipe,
    createSystem,
    mergeConfigs, defineRecipe,
} from '@chakra-ui/react'

const tokens = defineTokens({
    colors: {
        bg: {
            // Light theme (task: app-wide light theme rollout). Chat widget tokens
            // (chatTab/chatFrame/chatInput/chatOwner/chatBot/chatItem*) are a separate,
            // out-of-scope surface -- left untouched on purpose.
            primary: '#F6F6FB',
            secondary: '#FFFFFF',
            highlight: '#F9FAFB',
            card: "#FFFFFF",
            drawer: '#FFFFFF',
            input: "#FFFFFF",
            inputLight: "#F9FAFB",
            inputActive: "#FFFFFF",
            chatTab: "#2B2B2B",
            chatFrame: "#191919",
            chatInput: "#2D2D2F",
            chatOwner: "#8700DB",
            chatBot: "#3F3F3F",
            tab: "#FFFFFF",
            dark: "#F9FAFB",
            chatItemHover: "#ededed",
            chatItem: "#f6f6f6",
        },
        border: {
            primary: '#E5E7EB',
            secondary: '#D1D5DB',
            button: '#D1D5DB',
            inputLight: "#A78BFA",
        },
        text: {
            menu: "#6B7280",
            user: "#6B7280",
            grey: '#6B7280',
            chatDate: "#727272"
        },
        brand: {
            50: '#e0f2ff',
            100: '#b3dfff',
            200: '#80ccff',
            300: '#4db8ff',
            400: '#1aa5ff',
            500: '#008be6',
            600: '#006bb4',
            700: '#004c82',
            800: '#002d51',
            900: '#001020',
        },
        chat: {
            icon: "#A424F4",
            iconDisabled: "rgba(164,36,244,0.50)",
            input: "#2D2D2F"
        }
    },
    fonts: {
        body: 'Inter, sans-serif',
        heading: 'Inter, sans-serif',
    },
})

const buttonRecipe = defineRecipe({
    variants: {
        variant: {
            primary: {
                bgGradient: 'to-r',
                // Vars set by BrandingProvider.
                gradientFrom: 'var(--brand-from, #9D00FF)',
                gradientVia: 'var(--brand-via, #9000E9)',
                gradientTo: 'var(--brand-to, #8500D8)',
                color: 'var(--brand-on-primary, white)',
                fontSize: 16,
                fontWeight: 500,
                paddingX: 8,
                borderRadius: 8,
                textDecoration: 'unset',
                _hover: { filter: 'brightness(1.08)' }
            },
            outlined: {
                bg: "transparent",
                color: "gray.900",
                fontSize: 16,
                fontWeight: 500,
                borderWidth: 1,
                paddingX: 8,
                borderRadius: 8,
                borderColor: 'border.button',
                _hover: {bg: "bg.highlight", color: "gray.900"}
            },
            secondary: {bg: "green.500", color: "white"},
        },
    },
})

const inputRecipe = defineRecipe({
    variants: {
        variant: {
            primary: {
                bg: 'bg.inputActive',
                color: "gray.900",
                fontSize: 18,
                fontWeight: 400,
                borderRadius: 8,
                py: 6,
                border: '1px solid',
                borderColor: 'border.primary',
                _focus: {borderWidth: 2, borderColor: 'border.inputLight'},
                _disabled: {bg: 'bg.inputLight'},
            },
            secondary: {
                bg: 'bg.inputLight',
                color: "gray.900",
                fontSize: 18,
                fontWeight: 400,
                borderRadius: 8,
                border: '1px solid',
                borderColor: 'border.primary',
            },
            dark: {
                bg: 'bg.dark',
                color: "text.grey",
                padding: 2,
                fontSize: 14,
                fontWeight: 400,
                borderRadius: 8,
            },
        },
    },
})


const comboboxRecipe = defineSlotRecipe({
    className: "chakra-combobox",
    slots: ["root", "control", "input", "trigger", "content", "item"],
    base: {
        input: {
            width: "full",
            borderRadius: 8,
        },
    },
    variants: {
        visual: {
            ghosty: {
                control: {
                    borderRadius: 8,
                },
                input: {
                    px: 4,
                    py: 2,
                    bg: "bg.dark",
                    color: 'text.grey',
                    border: '1px solid',
                    borderColor: 'border.primary',
                },
                content: {
                    bg: "bg.tab",
                    boxShadow: "0 4px 24px rgba(23,21,31,0.12)",
                    borderRadius: 8,
                    border: '1px solid',
                    borderColor: 'border.primary',
                    p: 1,
                },
                item: {
                    color: 'gray.700',
                    _hover: { bg: "gray.100" },
                },
            },
            main: {
                control: {
                    borderRadius: 8,
                },
                input: {
                    px: 4,
                    py: 3,
                    color: 'text.grey',
                    border: '1px solid',
                    borderColor: 'border.primary',
                },
                content: {
                    bg: "bg.dark",
                    boxShadow: "0 4px 24px rgba(23,21,31,0.12)",
                    borderRadius: 8,
                    border: '1px solid',
                    borderColor: 'border.primary',
                    p: 1,
                },
                item: {
                    color: 'gray.700',
                    _hover: { bg: "gray.100" },
                },
            },
        },
    },
    defaultVariants: {
        visual: "ghosty",
    },
})

const selectRecipe = defineSlotRecipe({
    className: "chakra-select",
    slots: ["root", "control", "trigger", "content", "item", "valueText", "indicator"],
    base: {
        trigger: {
            width: "full",
            borderRadius: 8,
        },
    },
    variants: {
        visual: {
            ghosty: {
                trigger: {
                    px: 4,
                    py: 3.5,
                    bg: "bg.dark",
                    color: 'text.grey',
                    borderRadius: 8,
                    border: '1px solid',
                    borderColor: 'border.primary',
                    _hover: { bg: "gray.50" },
                },
                content: {
                    bg: "bg.tab",
                    boxShadow: "0 4px 24px rgba(23,21,31,0.12)",
                    borderRadius: 8,
                    border: '1px solid',
                    borderColor: 'border.primary',
                    p: 1,
                },
                item: {
                    color: 'gray.700',
                    borderRadius: 6,
                    _hover: { bg: "gray.100" },
                },
                valueText: {
                    color: 'text.grey',
                },
                indicator: {
                    color: 'text.grey',
                },
            },
        },
    },
    defaultVariants: {
        visual: "ghosty",
    },
})

const iconRecipe = defineRecipe({
    variants: {
        variant: {
            hoverable: {
                padding: 2,
                transition: "background 0.2s ease-in-out",
                cursor: "pointer",
                _hover: {
                    bg: 'gray.100',
                    borderRadius: 4,
                }
            },
        },
    },
})

const theme = defineConfig({
    theme: {
        tokens,
        recipes: {
            button: buttonRecipe,
            input: inputRecipe,
            icon: iconRecipe,
        },
        slotRecipes: {
            combobox: comboboxRecipe,
            select: selectRecipe,
        },
        keyframes: {
            bouncing: {
                '0%, 100%': {
                    transform: 'translateY(0px)'
                },
                '50%': {
                    transform: 'translateY(-8px)'
                }
            }
        }
    },
    globalCss: {
        "html, body": {
            bg: 'bg.primary',
            color: 'gray.900',
        },
        "::selection": {
            bg: "purple.400 !important",
            color: "white !important",
        },
        "::-moz-selection": {
            bg: "purple.400 !important",
            color: "white !important",
        },
        "@keyframes fadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
        },
        "@keyframes scaleIn": {
            from: { opacity: 0, transform: "scale(0.96) translateY(4px)" },
            to: { opacity: 1, transform: "scale(1) translateY(0)" },
        },
        "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(6px)" },
            to: { opacity: 1, transform: "translateY(0)" },
        },
    },
})

const config = mergeConfigs(defaultConfig, theme)
export const system = createSystem(config)
